"use client";

import { isServer } from "@decibeltrade/web-core/utils";
import React from "react";

import { Logger } from "@/utils/logger";

import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from "./session-storage";
import { StorageValue, UseStorageReturn } from "./types";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
  useLocalStorageSubscribe,
} from "./use-local-storage";

/**
 * useMergedStorage
 *
 * A hook that stores values in both localStorage and sessionStorage.
 * - Prioritizes sessionStorage when reading (for multi-tab scenarios)
 * - Falls back to localStorage if sessionStorage is empty
 * - When updating, writes to both storages (sessionStorage for current tab, localStorage as fallback)
 *
 * @example
 * const [value, setValue] = useMergedStorage("my-key", "default-value");
 *
 * @param key The key to store the value under
 * @param initialValue The initial value to store
 * @returns A tuple containing the current value and a function to update the value
 */

const getMergedStorageSnapshot = (key: string): string | null => {
  if (isServer()) return null;
  // Prioritize sessionStorage, fallback to localStorage
  const sessionValue = getSessionStorageItem(key);
  if (sessionValue !== null) return sessionValue;
  return getLocalStorageItem(key);
};

const getMergedStorageServerSnapshot = () => {
  return null;
};

const removeMergedStorageItem = (key: string) => {
  removeSessionStorageItem(key);
  removeLocalStorageItem(key);
};

const setMergedStorageItem = (key: string, value: StorageValue) => {
  setSessionStorageItem(key, value);
  setLocalStorageItem(key, value);
};

export function useMergedStorage<T extends StorageValue>(
  key: string,
  initialValue: T,
): UseStorageReturn<T> {
  const store = React.useSyncExternalStore(
    useLocalStorageSubscribe,
    () => getMergedStorageSnapshot(key),
    getMergedStorageServerSnapshot,
  );

  const setState = React.useCallback(
    (v: StorageValue | ((prevState: StorageValue) => StorageValue)) => {
      try {
        const currentValue: StorageValue = store
          ? (JSON.parse(store) as StorageValue)
          : initialValue;
        const nextState =
          typeof v === "function"
            ? (v as (prevState: StorageValue) => StorageValue)(currentValue)
            : v;

        // Write to both storages
        if (nextState === null) {
          removeMergedStorageItem(key);
        } else {
          setMergedStorageItem(key, nextState);
        }
      } catch (error) {
        Logger.error(error, {
          message: "Failed to set merged storage item",
          key,
          value: v,
        });
      }
    },
    [key, store, initialValue],
  );

  React.useEffect(() => {
    const sessionValue = getSessionStorageItem(key);
    const localValue = getLocalStorageItem(key);

    // Initialize both storages if needed
    if (sessionValue === null && localValue === null) {
      // Both empty - initialize with initialValue
      setSessionStorageItem(key, initialValue);
      setLocalStorageItem(key, initialValue);
    } else if (sessionValue === null && localValue !== null) {
      // sessionStorage empty but localStorage has value - sync sessionStorage
      setSessionStorageItem(key, JSON.parse(localValue) as StorageValue);
    } else if (sessionValue !== null && localValue === null) {
      // localStorage empty but sessionStorage has value - sync localStorage
      setLocalStorageItem(key, JSON.parse(sessionValue) as StorageValue);
    }
  }, [key, initialValue]);

  const parsedStore = React.useMemo(
    () => (store ? (JSON.parse(store) as T) : initialValue),
    [store, initialValue],
  );

  return [parsedStore, setState, store === null] as UseStorageReturn<T>;
}
